import { useMemo, useState } from 'react'
import { FaPlus } from 'react-icons/fa6'
import apiClient from '../../../services/apiClient'
import { DashboardImage } from '../AdminShared'
import { formatAdminMoney as formatMoney } from '../adminFormatters'
import { AdminMediaLibrary } from '../media/AdminMediaLibrary'

export function ConsignmentManager({ apiToken, rows = [], onExportCsv, onRefresh }) {
  const [selectedImage, setSelectedImage] = useState(null)
  const [isMediaOpen, setIsMediaOpen] = useState(false)
  const [view, setView] = useState('all')
  const [selectedIds, setSelectedIds] = useState([])
  const stats = useMemo(() => {
    const pendingReview = rows.filter((row) => ['submitted', 'photos needed', 'approved'].includes(row.status)).length
    const listed = rows.filter((row) => ['scheduled auction', 'buy now listing'].includes(row.sale)).length
    const conversionReady = rows.filter((row) => row.canConvert && row.workflowStage === 'Ready for decision').length
    const payoutDue = rows.filter((row) => row.rawPayoutStatus === 'manual-pending').length
    const payoutExposure = rows.reduce((sum, row) => row.rawPayoutStatus === 'manual-pending' ? sum + (row.rawPayoutAmount || row.rawExpectedPayoutAmount || 0) : sum, 0)

    return { pendingReview, listed, conversionReady, payoutDue, payoutExposure }
  }, [rows])

  const visibleRows = useMemo(() => {
    if (view === 'all') return rows

    return rows.filter((row) => {
      if (view === 'review') {
        return ['submitted', 'photos needed', 'approved'].includes(row.status) || row.workflowStage === 'Ready for decision'
      }

      if (view === 'listed') {
        return ['scheduled auction', 'buy now listing'].includes(row.sale)
      }

      if (view === 'payout') {
        return row.rawPayoutStatus === 'manual-pending' || row.workflowStage === 'Sale sold'
      }

      if (view === 'paid') {
        return row.rawPayoutStatus === 'paid' || row.workflowStage === 'Payout complete'
      }

      return true
    })
  }, [rows, view])

  const selectedRows = useMemo(() => rows.filter((row) => selectedIds.includes(row.id)), [rows, selectedIds])
  const visibleSelectedCount = useMemo(() => visibleRows.filter((row) => selectedIds.includes(row.id)).length, [selectedIds, visibleRows])
  const allVisibleSelected = visibleRows.length > 0 && visibleSelectedCount === visibleRows.length

  const toggleSelected = (id) => {
    setSelectedIds((current) => (
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id]
    ))
  }

  const toggleVisibleSelection = () => {
    setSelectedIds((current) => (
      allVisibleSelected
        ? current.filter((id) => !visibleRows.some((row) => row.id === id))
        : Array.from(new Set([...current, ...visibleRows.map((row) => row.id)]))
    ))
  }

  const clearSelection = () => setSelectedIds([])

  const bulkUpdate = async (update, message) => {
    if (!apiToken || !selectedRows.length) return

    try {
      const response = await apiClient.bulkUpdateConsignments(apiToken, {
        consignmentIds: selectedRows.map((row) => row.id),
        update,
      })
      clearSelection()
      await onRefresh?.(`${message}: ${response.updatedCount} consignments updated`)
    } catch (error) {
      await onRefresh?.(error.message || 'Bulk consignment update failed')
    }
  }

  const createConsignment = async (event) => {
    event.preventDefault()
    if (!apiToken) return

    const form = new FormData(event.currentTarget)
    const itemTitle = String(form.get('itemTitle') || '').trim()
    const consignorName = String(form.get('consignorName') || '').trim()

    if (!itemTitle || !consignorName) {
      await onRefresh?.('Item title and consignor name are required.')
      return
    }

    try {
      const images = selectedImage ? [selectedImage] : []
      await apiClient.createAdminConsignment(apiToken, {
        itemTitle,
        category: form.get('category') || 'Cards',
        consignorName,
        consignorId: form.get('consignorId') || '',
        estimate: Number(form.get('estimate') || 0),
        notes: form.get('notes') || '',
        status: form.get('status') || 'approved',
        saleStatus: 'pending-review',
        payoutStatus: 'not-started',
        images,
      })
      event.currentTarget.reset()
      setSelectedImage(null)
      await onRefresh?.('Admin consignment intake created')
    } catch (error) {
      await onRefresh?.(error.message || 'Admin consignment intake failed')
    }
  }

  const updateStatus = async (row, payload, message) => {
    if (!apiToken) return
    try {
      await apiClient.updateConsignment(apiToken, row.id, payload)
      await onRefresh?.(message)
    } catch (error) {
      await onRefresh?.(error.message || 'Consignment update failed')
    }
  }

  const convert = async (row, type) => {
    if (!apiToken) return
    try {
      await apiClient.convertConsignment(apiToken, row.id, {
        type,
        price: 250,
        reservePrice: 250,
        minimumIncrement: 25,
      })
      await onRefresh?.(`Consignment converted to ${type === 'auction' ? 'auction' : 'buy-now listing'}`)
    } catch (error) {
      await onRefresh?.(error.message || 'Consignment conversion failed')
    }
  }

  const markSold = async (row) => {
    const saleAmount = row.rawSaleAmount || row.rawEstimate || 250
    const payoutAmount = row.rawPayoutAmount || row.rawExpectedPayoutAmount

    await updateStatus(row, {
      status: 'sold',
      saleStatus: 'sold',
      payoutStatus: 'manual-pending',
      saleAmount,
      payoutAmount,
    }, 'Consignment marked sold')
  }

  const markPayoutPending = async (row) => {
    const saleAmount = row.rawSaleAmount || row.rawEstimate || 250
    const payoutAmount = row.rawPayoutAmount || row.rawExpectedPayoutAmount

    await updateStatus(row, {
      payoutStatus: 'manual-pending',
      saleAmount,
      payoutAmount,
    }, 'Payout queued for manual review')
  }

  const markPaid = async (row) => {
    const saleAmount = row.rawSaleAmount || row.rawEstimate || 250
    const payoutAmount = row.rawPayoutAmount || row.rawExpectedPayoutAmount

    await updateStatus(row, {
      status: 'sold',
      saleStatus: 'sold',
      payoutStatus: 'paid',
      saleAmount,
      payoutAmount,
    }, 'Consignor payout marked paid')
  }

  return (
    <section className="tactical-card obc-admin__builder">
      <div className="obc-admin__panel-head">
        <div>
          <h2>Consignment Tracker</h2>
          <span>Review intake, convert listings, track sales, and close manual payouts</span>
        </div>
        <button type="button" onClick={() => onExportCsv?.(apiToken, 'consignments', 'obc-consignments.csv', onRefresh)}>Export CSV</button>
      </div>

      <div className="obc-consignment-admin-summary">
        <article><strong>{stats.pendingReview}</strong><span>Review queue</span></article>
        <article><strong>{stats.conversionReady}</strong><span>Ready to list</span></article>
        <article><strong>{stats.listed}</strong><span>Listed inventory</span></article>
        <article><strong>{stats.payoutDue}</strong><span>Manual payouts</span></article>
        <article><strong>{formatMoney(stats.payoutExposure)}</strong><span>Payout exposure</span></article>
      </div>

      <div className="obc-consignment-admin-toolbar">
        <label>
          <span>Queue</span>
          <select value={view} onChange={(event) => setView(event.target.value)}>
            <option value="all">All consignments</option>
            <option value="review">Review queue</option>
            <option value="listed">Listed only</option>
            <option value="payout">Payout queue</option>
            <option value="paid">Paid complete</option>
          </select>
        </label>
        <label className="obc-consignment-admin-toolbar__select-all">
          <input type="checkbox" checked={allVisibleSelected} onChange={toggleVisibleSelection} />
          <span>Select visible ({visibleSelectedCount}/{visibleRows.length})</span>
        </label>
        <div className="obc-consignment-admin-actions">
          <button type="button" onClick={() => bulkUpdate({ status: 'approved', saleStatus: 'pending-review', payoutStatus: 'manual-pending' }, 'Bulk approved')} disabled={!selectedRows.length}>Approve Selected</button>
          <button type="button" onClick={() => bulkUpdate({ status: 'photos-needed' }, 'Photo request queued')} disabled={!selectedRows.length}>Need Photos</button>
          <button type="button" onClick={() => bulkUpdate({ payoutStatus: 'manual-pending' }, 'Payout review queued')} disabled={!selectedRows.length}>Payout Review</button>
          <button type="button" onClick={() => bulkUpdate({ status: 'sold', saleStatus: 'sold', payoutStatus: 'paid' }, 'Marked paid')} disabled={!selectedRows.length || selectedRows.some((row) => !(row.canMarkPaid || row.rawExpectedPayoutAmount))}>Mark Paid</button>
          {selectedIds.length ? <button type="button" onClick={clearSelection}>Clear</button> : null}
        </div>
      </div>

      <form className="obc-admin__form-grid obc-consignment-intake" onSubmit={createConsignment}>
        <label>Item title<input name="itemTitle" placeholder="1952 Topps Mantle PSA 3" required /></label>
        <label>Consignor name<input name="consignorName" placeholder="Consignor full name" required /></label>
        <label>Linked user ID<input name="consignorId" placeholder="Optional user id" /></label>
        <label>Category<select name="category" defaultValue="Cards"><option value="Cards">Cards</option><option value="Memorabilia">Memorabilia</option></select></label>
        <label>Estimate<input name="estimate" placeholder="1200" type="number" min="0" /></label>
        <label>Status<select name="status" defaultValue="approved"><option value="approved">Approved</option><option value="submitted">Submitted</option><option value="photos-needed">Photos needed</option></select></label>
        <label className="is-wide">Listing notes<input name="notes" placeholder="Condition, grade, provenance, reserve guidance, payout notes..." /></label>
        <button className="obc-admin__file-button" type="button" onClick={() => setIsMediaOpen(true)}>
          <FaPlus /> {selectedImage ? selectedImage.title || selectedImage.originalName || 'Image selected' : 'Choose Intake Media'}
        </button>
        <button className="obc-dashboard__bid-button" type="submit">Create Intake</button>
      </form>

      <AdminMediaLibrary
        apiToken={apiToken}
        open={isMediaOpen}
        onClose={() => setIsMediaOpen(false)}
        onSelect={setSelectedImage}
      />

      <div className="obc-consignment-admin-grid">
        {visibleRows.map((row) => (
          <article key={row.id}>
            <label className="obc-consignment-select">
              <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggleSelected(row.id)} />
              <span>Select</span>
            </label>
            <DashboardImage src={row.image} alt={row.item} />
            <div>
              <strong>{row.item}</strong>
              <span>{row.consignor} / {row.estimate}</span>
              <small><b className={`obc-order-stage is-${row.workflowTone || 'neutral'}`}>{row.workflowStage || row.status}</b> {row.status} / {row.sale} / {row.payout}</small>
              <small>Sale: {row.saleAmount} / Payout: {row.payoutAmount} / Expected: {row.expectedPayoutAmount}</small>
              <small>Source: {row.source} / Next: {row.nextAction}</small>
              <small>Paid: {row.paidAt}</small>
            </div>
            <ConsignmentTimeline steps={row.timeline} />
            <div className="obc-consignment-admin-actions">
              <button type="button" onClick={() => updateStatus(row, { status: 'approved', saleStatus: 'pending-review', payoutStatus: 'manual-pending' }, 'Consignment approved')}>Approve</button>
              <button type="button" onClick={() => updateStatus(row, { status: 'photos-needed' }, 'Photo request sent')}>Need Photos</button>
              <button type="button" onClick={() => convert(row, 'auction')} disabled={!row.canConvert}>Auction</button>
              <button type="button" onClick={() => convert(row, 'buy-now')} disabled={!row.canConvert}>Buy Now</button>
              <button type="button" onClick={() => markSold(row)}>Sold</button>
              <button type="button" onClick={() => markPayoutPending(row)}>Payout Due</button>
              <button type="button" onClick={() => markPaid(row)} disabled={!row.canMarkPaid && !row.rawExpectedPayoutAmount}>Paid</button>
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}

export function ConsignmentTimeline({ steps = [] }) {
  if (!steps.length) return null

  return (
    <div className="obc-consignment-timeline">
      {steps.map((step) => (
        <span className={`is-${step.state}`} key={step.code}>{step.label}</span>
      ))}
    </div>
  )
}

export function AdminConsignmentsModule({ apiToken, consignmentRows, onExportCsv, onRefresh }) {
  return <ConsignmentManager apiToken={apiToken} rows={consignmentRows} onExportCsv={onExportCsv} onRefresh={onRefresh} />
}
